import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { ArrowUpRight } from 'react-bootstrap-icons';
import { useAuth } from '../contexts/AuthContext';
import homeBlob from '../assets/home/home-blob.png';
import homeApp from '../assets/home/home-app.png';
import iconChat from '../assets/comingsoon/icon-chat.png';
import iconMap from '../assets/comingsoon/icon-map.png';
import iconTools from '../assets/comingsoon/icon-tools.png';
import iconIdeas from '../assets/comingsoon/icon-ideas.png';
import iconWorkshop from '../assets/comingsoon/icon-workshop.png';
import homeVisionScreenshot1 from '../assets/home/home-vision-screenshot-1.png';
import homeVisionScreenshot2 from '../assets/home/home-vision-screenshot-2.png';
import homeVisionScreenshot3 from '../assets/home/home-vision-screenshot-3.png';
import './LandingPage.css';
import LandingFooter from '../components/LandingFooter';
import LandingHeader from '../components/LandingHeader';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const handleTryNow = () => {
    navigate(currentUser ? '/home' : '/register');
  };

  return (
    <div className="landing-page">
      <LandingHeader />
      <section>
        <Container>
          <Row className="align-items-center min-vh-75">
            <Col lg={6}>
              <h1 className="hero-title">
                Turn Research into Figma Wireframes in Seconds
              </h1>
              <p className="hero-subtitle">
                Our context-aware engine learns your project's DNA so you don't have to prompt from scratch. Go from raw notes to logic-driven designs, ready for Figma.
              </p>
              <video
                className="hero-video"
                autoPlay
                muted
                loop
                controls
                playsInline
                src="https://firebasestorage.googleapis.com/v0/b/revealer-ai.firebasestorage.app/o/public%2Fvideo%2Fvideo-home-2.mp4?alt=media"
              />
              <div className="mt-4 d-flex">
                <Button
                  variant="tertiary"
                  onClick={handleTryNow}
                >
                  TRY REVEALER NOW <ArrowUpRight className="ms-2" />
                </Button>
              </div>
            </Col>
            <Col lg={6} className="d-flex justify-content-end">
              <div className="hero-images-container">
                <img src={homeBlob} alt="" className="img-fluid hero-blob" />
                <img src={homeApp} alt="" className="position-absolute img-fluid hero-app" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="vision-section vision-section-background py-5">
        <Container>
          <Row className="mb-5">
            <Col className="text-center">
              <h2 className="section-title text-center">Design Starts with Clarity.</h2>
              <p className="section-description text-center mx-auto">
                Stop drowning in raw transcripts. Transform your messy discovery notes into structured, high-fidelity wireframes in seconds—then export directly to Figma to finalize your design.
              </p>
              <Row className="vision-images-container g-4 justify-content-center align-items-center">
                <Col xs={12} md={4}>
                  <img src={homeVisionScreenshot2} alt="" className="img-fluid vision-section-image vision-section-image-side" />
                </Col>
                <Col xs={12} md={4}>
                  <img src={homeVisionScreenshot1} alt="" className="img-fluid vision-section-image vision-section-image-featured" />
                </Col>
                <Col xs={12} md={4}>
                  <img src={homeVisionScreenshot3} alt="" className="img-fluid vision-section-image vision-section-image-side" />
                </Col>
              </Row>
              <div className="vision-bridge-content">
                <h3 className="section-subtitle">The Bridge from Insight to Interface</h3>
                <ul>
                  <li><strong>From Raw Chaos to Ready-to-Polish:</strong> Stop formatting insights by hand. Our AI engine instantly transforms your transcripts and goals into architecturally sound wireframes ready for your creative touch.</li>
                  <li><strong>The Only Figma-Native Discovery Engine:</strong> Our custom plugin exports every layer directly into your workspace. Unlike static tools, Revealer AI provides a structural foundation that lets you move to high-fidelity polish without breaking your creative flow.</li>
                  <li><strong>Intelligence Beneath Your Intuition:</strong> Revealer AI acts as your co-creator, surfacing hidden patterns to sharpen your direction long before you open a design tool.</li>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="features-section py-5">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="section-title">Kickstart Your Workflow. Instantly.</h2>
              <p className="section-description">
                Stop waiting for genius to strike. Our templates give you the creative momentum to start your next project with clarity.
              </p>
            </Col>
          </Row>
          <Row className="g-4 justify-content-center">
            <Col md={6} lg={4}>
              <div className="difference-card feature-card">
                <div className="feature-icon mb-3">
                  <img src={iconChat} alt="Revealer AI" className="feature-card-icon" />
                </div>
                <h4>From Chat to Wireframe.</h4>
                <p>
                  Describe your project in a simple chat and instantly generate a foundational wireframe. Then, export directly to <b>Figma&trade;</b> for further refinement and collaboration.
                </p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="difference-card feature-card">
                <div className="feature-icon mb-3">
                  <img src={iconMap} alt="Revealer AI" className="feature-card-icon" />
                </div>
                <h4>Align Your Team with an Actionable Roadmap.</h4>
                <p>
                  Turn stakeholder notes and research findings into a clear, prioritized UX roadmap. Ensure your team is always aligned on the right next steps.
                </p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="difference-card feature-card">
                <div className="feature-icon mb-3">
                  <img src={iconTools} alt="Revealer AI" className="feature-card-icon" />
                </div>
                <h4>Break Through Creative Blocks.</h4>
                <p>
                  Discover new ideas and challenge assumptions with AI-powered  ideation. Generate concepts and structured feedback to find the best solutions.
                </p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="difference-card feature-card">
                <div className="feature-icon mb-3">
                  <img src={iconIdeas} alt="Revealer AI" className="feature-card-icon-large" />
                </div>
                <h4>Bring Your Users to Life.</h4>
                <p>
                  Transform research data into detailed user personas. Get a deeper understanding of your target audience's motivations and frustrations.
                </p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="difference-card feature-card">
                <div className="feature-icon mb-3">
                  <img src={iconWorkshop} alt="Revealer AI" className="feature-card-icon-large" />
                </div>
                <h4>Get Research-Based Design Feedback.</h4>
                <p>
                  Stop guessing and start validating. Get actionable, research-based feedback on your designs to build with confidence.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="vision-section enterprise-security-section py-5">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="section-title enterprise-text-white">Enterprise-Grade Security</h2>
              <p className="section-description enterprise-text-white">
                We take your data and privacy seriously. Revealer AI uses enterprise-grade security protocols. Your research and designs are safe with us.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="connect-section py-5 text-center">
        <Container>
          <h2 className="mb-4 section-title">Get Started Now</h2>
          <p className="mb-4">
            Get started with your first project. 
          </p>
          <Button
            variant="primary"
            onClick={handleTryNow}
          >
            TRY REVEALER NOW <ArrowUpRight className="ms-2" />
          </Button>
        </Container>
      </section>
      <LandingFooter />
    </div>
  );
};

export default LandingPage;